import React, { useState } from 'react';
import { Link, useLocation, Navigate } from 'react-router-dom';
import { useAdmin } from '../../contexts/AdminContext';
import { useAuth } from '../../contexts/SupabaseAuthContext';

interface AdminLayoutProps {
  children: React.ReactNode;
}

interface MenuItem {
  name: string;
  href: string;
  icon: React.ReactNode;
}

const iconProps = {
  className: 'w-[17px] h-[17px]',
  fill: 'none' as const,
  stroke: 'currentColor' as const,
  strokeWidth: 1.5,
  viewBox: '0 0 24 24',
};

const menuSections: { title: string; items: MenuItem[] }[] = [
  {
    title: 'Overview',
    items: [
      {
        name: 'Dashboard',
        href: '/admin',
        icon: (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
          </svg>
        ),
      },
    ],
  },
  {
    title: 'Catalog',
    items: [
      {
        name: 'Products',
        href: '/admin/products',
        icon: (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
          </svg>
        ),
      },
      {
        name: 'Image Triage',
        href: '/admin/image-triage',
        icon: (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
          </svg>
        ),
      },
      {
        name: 'Categories',
        href: '/admin/categories',
        icon: (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6.429 9.75L2.25 12l4.179 2.25m0-4.5l5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L21.75 12l-4.179 2.25m0 0l4.179 2.25L12 21.75 2.25 16.5l4.179-2.25m11.142 0l-5.571 3-5.571-3" />
          </svg>
        ),
      },
    ],
  },
  {
    title: 'Sales',
    items: [
      {
        name: 'Orders',
        href: '/admin/orders',
        icon: (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
          </svg>
        ),
      },
      {
        name: 'Coupons',
        href: '/admin/coupons',
        icon: (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
          </svg>
        ),
      },
      {
        name: 'Price Review',
        href: '/admin/price-review',
        icon: (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.518l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" />
          </svg>
        ),
      },
    ],
  },
  {
    title: 'Content',
    items: [
      {
        name: 'Notices',
        href: '/admin/notices',
        icon: (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535m0 0A23.74 23.74 0 0018.795 3m.38 1.125a23.91 23.91 0 011.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 001.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 010 3.46" />
          </svg>
        ),
      },
    ],
  },
  {
    title: 'Engagement',
    items: [
      {
        name: 'Reviews',
        href: '/admin/reviews',
        icon: (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
          </svg>
        ),
      },
      {
        name: 'Inquiries',
        href: '/admin/inquiries',
        icon: (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
          </svg>
        ),
      },
    ],
  },
];

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const { isAdminLoggedIn, isLoading: adminLoading, adminUser, logoutAdmin } = useAdmin();
  const { loading: authLoading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  if (authLoading || adminLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream-50">
        <div className="inline-flex items-center gap-2 text-warm-400">
          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v2a6 6 0 00-6 6H4z"></path>
          </svg>
          <span className="text-[11px] font-medium tracking-[0.12em] uppercase">Verifying</span>
        </div>
      </div>
    );
  }

  if (!isAdminLoggedIn) {
    return <Navigate to="/admin/login" replace />;
  }

  const isActive = (path: string) => {
    if (path === '/admin' && location.pathname === '/admin') return true;
    if (path !== '/admin' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const currentPageName = (() => {
    for (const section of menuSections) {
      const item = section.items.find(i => isActive(i.href));
      if (item) return item.name;
    }
    return 'Admin';
  })();

  return (
    <div className="h-screen flex bg-cream-50 font-sans">
      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } fixed inset-y-0 left-0 z-50 w-[236px] bg-cream-50 border-r border-warm-200/60 transform transition-transform duration-500 ease-spring lg:translate-x-0 lg:static lg:inset-auto flex flex-col`}
      >
        {/* Logo */}
        <div className="h-[52px] flex items-center px-5 border-b border-warm-200/60 flex-shrink-0">
          <Link to="/admin" className="flex items-baseline gap-0 group">
            <span className="font-display font-bold tracking-[-0.04em] text-forest-800 text-[19px] group-hover:text-forest-600 transition-colors">
              Libera
            </span>
            <span className="font-display font-bold tracking-[-0.04em] text-gold-400 text-[19px]">
              Cure
            </span>
            <span className="ml-2 text-[9px] font-semibold text-warm-400 uppercase tracking-[0.14em]">
              Admin
            </span>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          {menuSections.map((section, si) => (
            <div key={section.title} className={si > 0 ? 'mt-5' : ''}>
              <div className="px-3 mb-1.5 text-[9px] font-semibold text-warm-400 uppercase tracking-[0.12em]">
                {section.title}
              </div>
              <div className="space-y-[1px]">
                {section.items.map((item) => {
                  const active = isActive(item.href);
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setSidebarOpen(false)}
                      className={`relative flex items-center gap-2.5 px-3 py-[7px] text-[12.5px] font-medium rounded-lg transition-all duration-200 ${
                        active
                          ? 'bg-forest-50 text-forest-900'
                          : 'text-warm-500 hover:text-forest-800 hover:bg-cream-100'
                      }`}
                    >
                      {active && (
                        <span className="absolute left-0 top-[7px] bottom-[7px] w-[2px] bg-gold-400 rounded-r-full" />
                      )}
                      <span className={active ? 'text-forest-700' : 'text-warm-400'}>
                        {item.icon}
                      </span>
                      <span>{item.name}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer: user */}
        <div className="border-t border-warm-200/60 px-4 py-3 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-full bg-forest-50 border border-forest-100 flex items-center justify-center flex-shrink-0">
              <span className="text-[11px] font-semibold text-forest-700">
                {(adminUser?.name || 'A').charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[12px] font-semibold text-forest-900 truncate leading-tight">
                {adminUser?.name || 'Admin'}
              </div>
              <div className="text-[9px] text-warm-400 uppercase tracking-[0.1em] leading-tight mt-0.5">
                {adminUser?.role === 'admin' ? 'Super Admin' : 'Admin'}
              </div>
            </div>
            <button
              onClick={logoutAdmin}
              className="p-1.5 text-warm-400 hover:text-forest-700 hover:bg-cream-100 rounded-md transition-colors"
              title="Sign out"
            >
              <svg className="w-[14px] h-[14px]" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
              </svg>
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-forest-950/20 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main column */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Top bar */}
        <header className="h-[52px] bg-cream-50/90 backdrop-blur-xl border-b border-warm-200/60 flex items-center justify-between px-5 flex-shrink-0 sticky top-0 z-30">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-1.5 -ml-1.5 text-warm-500 hover:text-forest-800 rounded-md hover:bg-cream-100 transition-colors"
              aria-label="Toggle menu"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
              </svg>
            </button>
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-[10px] font-semibold text-warm-400 uppercase tracking-[0.12em] hidden sm:inline">
                Admin
              </span>
              <span className="text-warm-300 hidden sm:inline">/</span>
              <h1 className="text-[14px] font-semibold text-forest-900 tracking-tight truncate">
                {currentPageName}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-1">
            <Link
              to="/"
              target="_blank"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11.5px] font-medium text-warm-500 hover:text-forest-800 rounded-md hover:bg-cream-100 transition-colors"
            >
              <svg className="w-[13px] h-[13px]" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
              </svg>
              <span className="hidden sm:inline">View Site</span>
            </Link>
          </div>
        </header>

        {/* Content area */}
        <main className="flex-1 overflow-y-auto">
          <div className="px-6 py-6 lg:px-8 lg:py-7">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
